import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Box,
    Typography,
    LinearProgress,
    IconButton,
    useTheme,
    Divider,
    Paper
} from '@mui/material';
import {
    TimerOutlined as TimerIcon,
    ExitToApp as ExitToAppIcon,
    LockOpen as LockOpenIcon,
    Close as CloseIcon
} from '@mui/icons-material';

const InactivityPopup = ({ open, onStay, onLogout, autoLogoutTime = 30 }) => {
    const [timeLeft, setTimeLeft] = useState(autoLogoutTime);
    const theme = useTheme();

    // Timer countdown effect
    useEffect(() => {
        let timer;
        if (open && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft((prevTime) => prevTime - 1);
            }, 1000);
        } else if (timeLeft === 0 && open) {
            onLogout();
        }

        return () => {
            clearInterval(timer);
        };
    }, [open, timeLeft, onLogout]);

    // Reset timer when dialog opens
    useEffect(() => {
        if (open) {
            setTimeLeft(autoLogoutTime);
        }
    }, [open, autoLogoutTime]);

    // Calculate percentage for progress indicator
    const progressPercentage = (timeLeft / autoLogoutTime) * 100;

    // Color logic - transition from blue to red as time decreases
    const getColor = () => {
        if (progressPercentage > 60) return theme.palette.primary.main;
        if (progressPercentage > 30) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    const currentColor = getColor();

    return (
        <Dialog
            open={open}
            aria-labelledby="inactivity-dialog-title"
            aria-describedby="inactivity-dialog-description"
            PaperProps={{
                elevation: 24,
                sx: {
                    borderRadius: 1,
                    minWidth: '380px',
                    maxWidth: '420px',
                    overflow: 'hidden',
                    backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0.98))',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }
            }}
        >
            {/* Progress Bar at the top */}
            <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{
                    height: 4,
                    bgcolor: theme.palette.grey[100],
                    '& .MuiLinearProgress-bar': {
                        bgcolor: currentColor
                    }
                }}
            />

            <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
                <IconButton
                    size="small"
                    onClick={onStay}
                    sx={{
                        color: theme.palette.grey[500],
                        '&:hover': {
                            color: theme.palette.grey[700]
                        }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogTitle
                id="inactivity-dialog-title"
                sx={{
                    pt: 3,
                    pb: 1,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: theme.palette.text.primary
                }}
            >
                <TimerIcon sx={{ color: currentColor }} />
                <Typography variant="h6" component="span" fontWeight="500">
                    Session Timeout
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }}>
                <Typography
                    variant="body1"
                    component="div"
                    sx={{
                        color: theme.palette.text.secondary,
                        mb: 2
                    }}
                >
                    Your session is about to expire due to inactivity. You will be logged out in:
                </Typography>

                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 2,
                        px: 3,
                        mb: 2,
                        bgcolor: theme.palette.grey[50],
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 1,
                        borderLeft: `4px solid ${currentColor}`,
                    }}
                >
                    <Typography
                        variant="h4"
                        component="div"
                        sx={{
                            fontFamily: 'monospace',
                            fontWeight: 'bold',
                            color: currentColor,
                            letterSpacing: 1
                        }}
                    >
                        {`00:${timeLeft.toString().padStart(2, '0')}`}
                    </Typography>
                </Paper>

                <Typography
                    variant="body2"
                    sx={{
                        color: theme.palette.text.secondary,
                        fontStyle: 'italic'
                    }}
                >
                    For security reasons, your session will automatically timeout after periods of inactivity.
                </Typography>
            </DialogContent>

            <Divider />

            <DialogActions sx={{
                p: 2,
                justifyContent: 'flex-end',
                gap: 1
            }}>
                <Button
                    onClick={onLogout}
                    color="inherit"
                    variant="text"
                    size="medium"
                    startIcon={<ExitToAppIcon />}
                    sx={{
                        color: theme.palette.text.secondary
                    }}
                >
                    Log Out
                </Button>
                <Button
                    onClick={onStay}
                    color="primary"
                    variant="contained"
                    disableElevation
                    size="medium"
                    startIcon={<LockOpenIcon />}
                    sx={{
                        px: 2,
                        boxShadow: 'none',
                        borderRadius: 1,
                        fontWeight: 500
                    }}
                >
                    Stay Active
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InactivityPopup;